import type Phaser from "phaser";
import { CANVAS_COLOURS, type WashRow } from "daedalus-core";

/**
 * The player wash (ADR-071 decision 3): one strip per collision cell row of the map, the map's full width,
 * each at the depth `washRows` gives, so whatever sorts over a character standing on that row draws over
 * its green and whatever such a character covers is tinted by it. Each strip is an `Image` of one 1 by 1
 * white texture, tinted and scaled, so the strips draw in the same kind of batch as the props' images.
 * Strips are made on the first layout, laid out again only when the map's size or band changes, and hidden
 * rather than destroyed when the wash is turned off; outside the camera's rows they are hidden too, which
 * changes no depth and so sorts nothing again.
 */

/**
 * The wash's colour, `overlay-player-wash` from the canvas colour table.
 */
export const WASH_COLOUR = CANVAS_COLOURS.playerWash.rgb;

/**
 * The wash's alpha: the colour's own alpha drawn at 35%.
 */
export const WASH_ALPHA = CANVAS_COLOURS.playerWash.alpha * 0.35;

/**
 * The fill of the texture the strips tint: white, so a tint multiplies to exactly the wash's colour. It is
 * the neutral base of a tint, never a colour the map shows, so it is not a canvas colour.
 */
const TINT_BASE = "white";

/**
 * The key of the 1 by 1 white texture every strip draws.
 */
export const WASH_TEXTURE = "daedalus:wash-white";

/**
 * The map a layout was made for: its width in pixels, its height in tiles and its band.
 */
interface WashLayoutKey {
  readonly width: number;
  readonly mapHeight: number;
  readonly band: number;
}

/**
 * The player wash of one scene.
 */
export class PlayerWash {
  private readonly strips: Phaser.GameObjects.Image[] = [];
  private key: WashLayoutKey | null = null;
  private cell = 0;
  private shown = false;
  private first = 0;
  private last = -1;
  private laidOut = 0;

  /**
   * @param scene Scene to draw into.
   */
  public constructor(private readonly scene: Phaser.Scene) {}

  /**
   * How many times the strips were laid out, which only a first show and a change of the map's size or band
   * do.
   *
   * @returns The count.
   */
  public get layouts(): number {
    return this.laidOut;
  }

  /**
   * Whether the wash is shown.
   *
   * @returns True while shown.
   */
  public get visible(): boolean {
    return this.shown;
  }

  /**
   * How many strips exist.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.strips.length;
  }

  /**
   * Shows or hides the wash, laying its strips out when it is shown for a map it was not laid out for.
   *
   * @param shown Whether shown.
   * @param map The map's width in pixels, its height in tiles and its band.
   * @param rows The layout, asked for only when the strips are laid out again (`washRows`).
   */
  public sync(shown: boolean, map: WashLayoutKey, rows: () => readonly WashRow[]): void {
    if (shown && (this.key === null || this.key.width !== map.width || this.key.mapHeight !== map.mapHeight || this.key.band !== map.band)) {
      this.layout(rows(), map.width);
      this.key = { width: map.width, mapHeight: map.mapHeight, band: map.band };
    }
    if (shown === this.shown) {
      return;
    }
    this.shown = shown;
    for (let i = this.first; i <= this.last && i < this.strips.length; i += 1) {
      this.strips[i]?.setVisible(shown);
    }
  }

  /**
   * Shows only the strips a band of world rows crosses, touching the strips that enter or leave it, so a
   * pan or a zoom lays nothing out.
   *
   * @param top The view's top in world pixels.
   * @param bottom The view's bottom in world pixels.
   */
  public cull(top: number, bottom: number): void {
    if (this.cell <= 0) {
      return;
    }
    const first = Math.max(0, Math.floor(top / this.cell));
    const last = Math.min(this.strips.length - 1, Math.floor(bottom / this.cell));
    if (first === this.first && last === this.last) {
      return;
    }
    for (let i = this.first; i <= this.last && i < this.strips.length; i += 1) {
      if (i < first || i > last) {
        this.strips[i]?.setVisible(false);
      }
    }
    for (let i = first; i <= last; i += 1) {
      if (i < this.first || i > this.last) {
        this.strips[i]?.setVisible(this.shown);
      }
    }
    this.first = first;
    this.last = last;
  }

  /**
   * Removes every strip.
   */
  public destroy(): void {
    for (const strip of this.strips) {
      strip.destroy();
    }
    this.strips.length = 0;
    this.key = null;
    this.cell = 0;
    this.first = 0;
    this.last = -1;
  }

  /**
   * Places a strip for each row, reusing the strips there are and destroying the ones left over; every
   * strip starts hidden until `cull` shows the camera's rows.
   *
   * @param rows The strips' tops, heights and depths.
   * @param width The map's width in pixels.
   */
  private layout(rows: readonly WashRow[], width: number): void {
    this.ensureTexture();
    rows.forEach((row, i) => {
      let strip = this.strips[i];
      if (strip === undefined) {
        strip = this.scene.add.image(0, 0, WASH_TEXTURE).setOrigin(0, 0).setTint(WASH_COLOUR).setAlpha(WASH_ALPHA);
        this.strips.push(strip);
      }
      strip.setPosition(0, row.top).setScale(width, row.height).setDepth(row.depth).setVisible(false);
    });
    for (const strip of this.strips.splice(rows.length)) {
      strip.destroy();
    }
    this.cell = rows[0]?.height ?? 0;
    this.first = 0;
    this.last = -1;
    this.laidOut += 1;
  }

  /**
   * Makes the 1 by 1 white texture the strips draw, once per game.
   *
   * @throws Error when the texture cannot be created.
   */
  private ensureTexture(): void {
    if (this.scene.textures.exists(WASH_TEXTURE)) {
      return;
    }
    const texture = this.scene.textures.createCanvas(WASH_TEXTURE, 1, 1);
    if (texture === null) {
      throw new Error(`texture ${WASH_TEXTURE} could not be created`);
    }
    const context = texture.getContext();
    context.fillStyle = TINT_BASE;
    context.fillRect(0, 0, 1, 1);
    texture.refresh();
  }
}
